import Hls from 'hls.js';

/**
 * Attaches an HLS stream to one `<audio>` element.
 *
 * ## Two completely different code paths, and both are needed
 *
 * hls.js works by feeding segments into Media Source Extensions. **iOS Safari
 * has no MSE at all**, so hls.js does not work there in any version - but
 * Safari and iOS play HLS natively from a plain `src`, which no other browser
 * does. So the rule is: if the browser can play the manifest itself, hand it
 * over; otherwise use hls.js (HANDOFF §9.1).
 *
 * Feature-detected rather than sniffed for a browser name, because the property
 * that matters is exactly what `canPlayType` answers.
 */
export interface Engine {
  destroy: () => void;
}

export function canPlayNatively(audio: HTMLAudioElement): boolean {
  // Safari returns "maybe" or "probably"; everything else returns "".
  return audio.canPlayType('application/vnd.apple.mpegurl') !== '';
}

export function attach(audio: HTMLAudioElement, url: string, onError: (message: string) => void): Engine {
  if (canPlayNatively(audio)) {
    audio.src = url;

    return { destroy: () => {
      audio.removeAttribute('src');
      audio.load();
    } };
  }

  if (!Hls.isSupported()) {
    onError('This browser cannot play streamed audio.');

    return { destroy: () => {} };
  }

  const hls = new Hls({
    /*
     * Without this the media cookie is not sent and every segment 403s
     * (HANDOFF §4.4).
     *
     * In production the audio lives on `media.<brand>` while the player is on
     * the app origin, so segment requests are cross-origin and a browser omits
     * credentials from those unless asked. In development they are same-origin
     * through Vite's proxy and this changes nothing - which is precisely why it
     * is easy to omit and only discover after a deploy.
     */
    xhrSetup: (xhr) => {
      xhr.withCredentials = true;
    },

    /*
     * Audio-only, so the defaults tuned for video are wrong.
     *
     * A 30-second forward buffer on a 4-minute track at 192k is under a
     * megabyte - cheap, and it means a brief connection drop is inaudible
     * rather than a stall. hls.js defaults to 30 for video where the same
     * duration is far more data.
     */
    maxBufferLength: 30,
    // Start on the lower rendition and let it climb. The first seconds of a
    // track are when somebody decides whether to keep listening, so time to
    // first note matters more than fidelity for those seconds.
    startLevel: 0,
    enableWorker: true,
  });

  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (!data.fatal) {
      // Non-fatal errors are routine - a segment that needs one retry, a gap
      // in the buffer. hls.js recovers from them on its own and surfacing them
      // would mean showing an error during normal playback.
      return;
    }

    switch (data.type) {
      case Hls.ErrorTypes.NETWORK_ERROR:
        /*
         * A 403 here almost always means an expired media token rather than a
         * genuine network failure - the tokens last six hours and a long
         * listening session can outlive one.
         *
         * `startLoad()` retries, and by then the caller will have re-minted.
         */
        hls.startLoad();
        break;

      case Hls.ErrorTypes.MEDIA_ERROR:
        // A decoding hiccup. hls.js can usually re-sync the buffer without
        // tearing the whole stream down.
        hls.recoverMediaError();
        break;

      default:
        hls.destroy();
        onError('Playback failed.');
    }
  });

  hls.loadSource(url);
  hls.attachMedia(audio);

  return { destroy: () => hls.destroy() };
}
